// pages/NavbotAdminPanel.js
// Admin tab for the KB Navigator (navbot). Central place to turn its guided
// tours on/off + control auto-open. Tours come from the shared registry
// (client/src/lib/tours.js); each is gated by a boolean flag in
// /api/app-settings that the NavBot reads on mount. Add a tour there and its
// toggle appears here automatically.

import { useEffect, useState } from 'react';
import ToggleSwitch from './sections/ToggleSwitch';
import { TOUR_REGISTRY } from '../lib/tours';
import './NavbotAdminPanel.css';

export default function NavbotAdminPanel({ flash }) {
  const [settings, setSettings] = useState(null);   // null = loading
  const [savingKey, setSavingKey] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/app-settings', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(s => { if (!cancelled) setSettings(s || {}); })
      .catch(e => { if (!cancelled) { setErr(e.message); setSettings({}); } });   // show defaults
    return () => { cancelled = true; };
  }, []);

  async function save(key, next, label) {
    const prev = settings ? settings[key] : undefined;
    setSavingKey(key);
    setSettings(s => ({ ...(s || {}), [key]: next }));   // optimistic
    try {
      const r = await fetch('/api/app-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: next }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const s = await r.json();
      setSettings(s);
      if (flash) flash(`${label} ${next ? 'enabled' : 'disabled'}.`);
    } catch (e) {
      setSettings(s => ({ ...(s || {}), [key]: prev }));   // revert
      if (flash) flash(`Couldn't update setting: ${e.message}`, true);
    } finally {
      setSavingKey(null);
    }
  }

  const loading = settings === null;
  const autoOpenOn = settings ? settings.navbotTourAutoOpen !== false : true;   // default on

  return (
    <div className="admin-panel">
      <h3>KB Navigator</h3>
      <p className="admin-hint">
        Turn the KB Navigator’s guided tours on or off. Each tour walks users through a feature over a
        curated demo dataset (no real data is touched). Changes take effect for each user on their next
        page load.
      </p>

      {err && (
        <div className="admin-flash error" style={{ marginBottom: 12 }}>
          Couldn’t load settings ({err}) — showing defaults. Saving will retry.
        </div>
      )}

      <div className="navbot-tours">
        {TOUR_REGISTRY.map(tour => {
          const on = settings ? settings[tour.flag] !== false : true;   // default on
          return (
            <div key={tour.id} className="navbot-tour-row">
              <div className="navbot-tour-row-text">
                <strong>{tour.label} tour</strong>
                <div className="navbot-tour-tags">
                  <span className="navbot-tag navbot-tag--section" title="Section this tour lives on">
                    {tour.sectionKey}
                  </span>
                  <span className="navbot-tag navbot-tag--period" title="Period scope">
                    {tour.period || 'All periods'}
                  </span>
                </div>
                <p>{tour.description}</p>
              </div>
              <ToggleSwitch
                checked={on}
                saving={savingKey === tour.flag}
                disabled={loading}
                onChange={(v) => save(tour.flag, v, `${tour.label} tour`)}
                label={loading ? 'Loading…' : on ? 'On' : 'Off'}
              />
            </div>
          );
        })}

      </div>

      <h4 className="navbot-admin-subhead">Behavior</h4>
      <div className="navbot-tours">
        <div className="navbot-tour-row">
          <div className="navbot-tour-row-text">
            <strong>Auto-open the welcome page</strong>
            <p>
              When on, a tour’s welcome page opens automatically the first time each session a user
              lands on its section page — they can dismiss it with “Not now”. Turn off to require the
              ✨ button. Applies to every enabled tour above.
            </p>
          </div>
          <ToggleSwitch
            checked={autoOpenOn}
            saving={savingKey === 'navbotTourAutoOpen'}
            disabled={loading}
            onChange={(v) => save('navbotTourAutoOpen', v, 'Auto-open tour welcome')}
            label={loading ? 'Loading…' : autoOpenOn ? 'On' : 'Off'}
          />
        </div>
      </div>

    </div>
  );
}
